<template>
  <div class="h-100">
    <confirm id="confirmDeleteOrder" ref="confirmDeleteOrder" @confirm="deleteOrder">
      <template slot="title">
        <span>{{ t('clients-detail-confirm_delete_order_title') }}</span>
      </template>
      <template slot="body">
        <div>{{ t('clients-detail-confirm_delete_event_body') }}</div>
      </template>
    </confirm>
    <confirm id="confirmDeleteCustomEvent" ref="confirmDeleteCustomEvent" @confirm="deleteCustomEvent">
      <template slot="title">
        <span>{{ t('clients-detail-confirm_delete_custom_event_title') }}
          <b v-if="toDeleteCustomEvent">{{ toDeleteCustomEvent.title }}</b>?</span>
      </template>
      <template slot="body">
        <div>{{ t('clients-detail-confirm_delete_event_body') }}</div>
      </template>
    </confirm>
    <modal ref="updateOrder" :startsOpen="false" id="updateOrder" v-bind:class="{ 'modal-wide': false }">
      <template v-if="toUpdateOrder" slot="title">
        {{ t('clients-detail-edit_order') }}<br />
        <small>{{ t('clients-detail-made_order_of') }}
          <strong>{{ formatCurrency(toUpdateOrder.value) }}</strong></small>
      </template>
      <template slot="body">
        <add-order v-if="toUpdateOrder && ['insert'].includes(dashboardLevel)" @orderSaved="afterUpdateOrder($event)" form-type="update" :show-title="false" :to-update-order="toUpdateOrder" :default-phone="client.whatsapp" />
      </template>
    </modal>

    <div class="gap-lg h-100 timeline-wrapper" style="margin-top:-10px;">

      <div class="d-flex flex-column gap-md">

        <template v-if="isInitialLoading">
          <div class="d-flex gap-sm align-items-end">
            <KvSkeleton width="200px" height="40px" style="border-radius: 8px;" />

            <div class="d-flex gap-sm">
              <KvSkeleton width="100px" height="32px" style="border-radius: 16px;" />
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <KvSkeleton width="28px" height="28px" />
            <KvSkeleton width="150px" height="16px" />
          </div>
        </template>

        <template v-else>
          <div class="d-flex gap-sm align-items-end">
            <KvInput size="medium" :placeholder="t('clients-timeline-search')" v-model="search" class="flex-shrink-0" style="max-width: 300px;">
              <template #prepend>
                <Icon icon-class="ui-search-01" size="4" color="#9DA4AE" />
              </template>
            </KvInput>

            <div class="flex-1">
              <KvFilter :has-branch-filter="false" :has-period-filter="false" @changed="updateFilters" ref="filterBar" :filters="extraFilters"></KvFilter>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <KvTooltip :text="allCollapsed ? t('clients-timeline-open_all') : t('clients-timeline-close_all')" placement="top">
              <KvButton @click="toggleAll" variant="text" size="small">
                <svg v-if="allCollapsed" width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="8.75" y1="7.91666" x2="19.25" y2="7.91666" stroke="#384250" stroke-width="1.5" stroke-linecap="round" />
                  <line x1="8.75" y1="19.9167" x2="19.25" y2="19.9167" stroke="#384250" stroke-width="1.5" stroke-linecap="round" />
                  <path d="M11.3333 14.8333L14 17.3333L16.6666 14.8333M11.3333 13.1667L14 10.6667L16.6666 13.1667" stroke="#384250" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>

                <svg v-else width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.6667 7.83334L14 10.3333L11.3334 7.83334M11.3334 20.1667L14 17.6667L16.6667 20.1667" stroke="#384250" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <line x1="8.75" y1="12.5833" x2="19.25" y2="12.5833" stroke="#384250" stroke-width="1.5" stroke-linecap="round" />
                  <line x1="8.75" y1="15.25" x2="19.25" y2="15.25" stroke="#384250" stroke-width="1.5" stroke-linecap="round" />
                </svg>
              </KvButton>
            </KvTooltip>

            <KvTypography color="primary" v-if="timeline && eventsWithMeta.length > 0">
              {{ t('clients-timeline-showing_range', [(currentPage - 1) * limit + 1, Math.min(currentPage * limit, total), total]) }}
            </KvTypography>
          </div>
        </template>
      </div>

      <KvScrollbar ref="scrollbar" contentClass="events-timeline">

        <template v-if="loading">
          <div v-for="i in 5" :key="i" class="event-item gap-md">
            <div class="event-icon-wrapper h-100 d-flex align-items-center">
              <div class="event-icon" style="background: transparent;">
                <KvSkeleton width="33px" height="33px" style="border-radius: 50%" />
              </div>
            </div>
            <div class="event-content">
              <KvSkeleton height="100px" width="100%" style="border-radius: 8px" />
            </div>
          </div>
        </template>

        <template v-else-if="timeline && eventsWithMeta.length > 0">
          <div v-for="event in eventsWithMeta" class="event-item gap-md" :key="event.id">
            <div class="event-icon-wrapper h-100 d-flex align-items-center">
              <div class="event-icon" :style="{
                color: event.meta.textColor,
                backgroundColor: event.meta.color
              }">
                <span v-if="event.meta.customIcon" :title="event.meta.name" :class="event.meta.customIcon" />
                <Icon v-else-if="event.meta.icon" :title="event.meta.name" :icon-class="event.meta.icon" />
                <KvTypography v-else color="inherit" weight="semibold">
                  {{ event.grade ?? '?' }}
                </KvTypography>
              </div>
            </div>

            <KvCard v-if="event.type === 'custom-event'" collapsible class="event-content" variant="soft" :id="`${event.type}-${event.id}`" density="compact" v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm">
                  <div class="d-flex align-items-center gap-sm">
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                    <Icon v-if="event.place_branch" icon-class="ui-marker-pin-02" size="2" color="#209941" />
                    <!-- i18n-validator-ignore-next-line: runtime branch name -->
                    {{ event.place_branch }}
                  </div>
                  <KvTypography color="primary" size="small" weight="medium" :truncate="!openCards[`${event.type}-${event.id}`]">
                    <!-- i18n-validator-ignore-next-line: runtime event author -->
                    {{ event.author }}
                    {{ t('clients-detail-registered_event') }} <!-- i18n-validator-ignore-next-line: runtime event title -->{{ event.title }}
                  </KvTypography>
                </div>
              </template>
              <template #actions>
                <KvButton variant="outlined" size="small" prepend-icon="ui-x-close" @click.prevent="confirmDeleteCustomEvent(event)">{{ t('clients-timeline-delete') }}</KvButton>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'order'" collapsible class="event-content" variant="soft" :id="event.id" density="compact" v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm">
                  <div>
                    <div class="d-flex align-items-center gap-sm flex-wrap">
                      <KvChip v-if="event.status === 'cancelled'" color="error" density="extra-compact">{{ t('clients-detail-order_cancelled') }}</KvChip>
                      <KvChip v-else color="success" density="extra-compact">{{ t('clients-timeline-order_created') }}</KvChip>
                      <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                      <Icon icon-class="ui-marker-pin-02" size="2" color="#209941" />
                      <!-- i18n-validator-ignore-next-line: runtime branch name -->
                      {{ event.branch }}
                    </div>
                    <KvChip v-if="event.is_delivery === '1'" rounded="soft" density="compact">
                      {{ t('clients-detail-delivery') }}
                    </KvChip>
                  </div>
                  <KvTypography size="small" weight="medium">{{ t('clients-detail-made_order_of') }} {{ formatCurrency(event.value) }}<template v-if="event.original_currency && event.original_currency !== placeCurrency"> ({{ formatCurrency(event.original_value, event.original_currency) }})</template></KvTypography>
                </div>
              </template>
              <template #text>
                <div class="d-flex flex-column gap-md align-items-start">
                  <div v-if="event.products && event.products.length > 0">
                    <KvTypography color="secondary" size="small">{{ t('clients-detail-products') }}</KvTypography>
                    <ul class="list">
                      <li v-for="product in event.products">
                        <KvTypography color="secondary">{{ formatQuantity(product.quantity) }} <!-- i18n-validator-ignore-next-line: runtime product name -->{{ product.name }}<span v-if="product.category" class="product-category"> ({{ product.category }})</span></KvTypography>
                      </li>
                    </ul>
                  </div>
                  <div v-if="getOrderFieldEntries(event).length > 0">
                    <KvTypography color="secondary" size="small">{{ t('clients-timeline-order_fields') }}</KvTypography>
                    <ul class="list">
                      <li v-for="entry in getOrderFieldEntries(event)" :key="entry.id">
                        <KvTypography color="secondary">
                          <!-- i18n-validator-ignore-next-line: runtime order field label -->{{ entry.field_name }}: <strong>{{ formatOrderFieldDisplay(entry) }}</strong>
                        </KvTypography>
                      </li>
                    </ul>
                  </div>
                  <KvChip v-if="event.generatedPoints" rounded="soft" density="compact">
                    {{ t('clients-detail-earned') }}
                    <KvTypography size="small" color="primary" weight="medium">{{
                      printPoints(event.generatedPoints)
                    }}</KvTypography>
                  </KvChip>
                </div>
              </template>
              <template v-if="$module('point_of_sale') && ['insert'].includes(dashboardLevel)" #actions>
                <KvButton @click="confirmDeleteOrder(event)" variant="outlined" size="small" prepend-icon="ui-x-close">{{ t('clients-timeline-delete') }}</KvButton>
                <KvButton v-if="!event.external_id" @click="modalUpdateOrder(event)" size="small" prepend-icon="ui-edit-05">
                    {{ t('clients-detail-edit') }}
                </KvButton>
              </template>
            </KvCard>

            <KvCard v-else-if="$module('quotations') && event.type === 'quotation'" collapsible class="event-content" variant="soft" density="compact" v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <div class="d-flex align-items-center gap-sm flex-wrap">
                    <KvChip v-if="['expired', 'rejected'].includes(event.status)" color="error" rounded="soft" density="compact">
                      {{ t('clients-detail-quotation') }} {{ quotationStatusLabel[event.status] }}
                    </KvChip>
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                    <Icon v-if="event.branch" icon-class="ui-marker-pin-02" size="2" color="#209941" />
                    <!-- i18n-validator-ignore-next-line: runtime branch name -->
                    {{ event.branch }}
                  </div>
                  <KvTypography size="small" weight="medium">{{ t('clients-detail-has_quotation_of') }} {{ formatCurrency(event.value) }}</KvTypography>
                </div>

              </template>
              <template #text>
                <div class="d-flex flex-column gap-md align-items-start">
                  <div>
                    <KvTypography color="secondary">{{ t('clients-detail-products') }}</KvTypography>
                    <ul class="list">
                      <li v-for="product in event.products">
                        <KvTypography color="secondary">{{ formatQuantity(product.quantity) }} <!-- i18n-validator-ignore-next-line: runtime product name -->{{ product.name }}<span v-if="product.category" class="product-category"> ({{ product.category }})</span></KvTypography>
                      </li>
                    </ul>
                  </div>
                  <KvChip v-if="event.order.id" rounded="soft" density="compact" role="button" @click="scrollToElement(event.order.id)">
                    <KvTypography color="secondary">{{ t('clients-timeline-order_created_label') }} <KvTypography color="secondary" weight="medium">
                        <HumanDate :date="event.order.created" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf"></HumanDate>
                      </KvTypography>
                    </KvTypography>
                  </KvChip>

                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'feedback'" collapsible class="event-content" variant="soft" density="compact" :id="event.id" v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm">
                  <div class="d-flex align-items-center gap-sm">
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                    <Icon v-if="event.branch" icon-class="ui-marker-pin-02" size="2" color="#209941" />
                    {{ event.branch }}
                  </div>

                  <!-- Plain click opens the answer in the drawer right here; the
                       href to the inbox stays for ctrl/cmd/middle click. -->
                  <a v-if="event.hasAccess" :href="answerInboxHref(event.id)" @click="openAnswer(event.id, $event)">
                    <KvTypography hover color="primary" size="small" weight="medium">{{ t('clients-detail-left_feedback') }} {{ event.grade ? `${t('clients-detail-with_average')} ${event.grade}` : "" }}</KvTypography>
                  </a>
                  <span v-else>
                    <KvTypography hover color="primary" size="small" weight="medium">{{ t('clients-detail-left_feedback') }} {{ event.grade ? `${t('clients-detail-with_average')} ${event.grade}` : "" }}</KvTypography>
                  </span>
                </div>
              </template>
              <template #text>
                <div class="d-flex flex-column gap-md">
                  <KvTypography color="primary" v-if="event.form">
                    {{ t('clients-detail-using_form') }} <!-- i18n-validator-ignore-next-line: runtime form name -->{{ event.form }}
                  </KvTypography>
                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'segment'" class="event-content" variant="text" density="compact">
              <template #text>
                <KvTypography size="extra-small" color="muted" class="d-flex align-items-center gap-sm">
                  <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                </KvTypography>
                <div class="d-flex flex-column gap-md">
                  <KvTypography color="secondary">
                    {{ event.action === "left" ? t('clients-detail-left_segment') : t('clients-detail-entered_segment') }}
                    {{ t('clients-detail-segment') }}
                    <KvTypography color="inherit" weight="medium">
                      <!-- i18n-validator-ignore-next-line: runtime segment name -->
                      {{ event.segmentName }}
                    </KvTypography>
                  </KvTypography>
                </div>
              </template>
            </KvCard>
            <KvCard v-else-if="event.type === 'email'" class="event-content" variant="soft" density="compact"  v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <div class="d-flex align-items-center gap-sm flex-wrap">
                    <NotificationStatusBadge :status="event.status" :fail-reason="event.fail_reason" :delivered-at="event.delivered_at" :read-at="event.read_at" :replied-at="event.replied_at" />
                    <NotificationRecipientChip :address="event.recipient_address" :method="event.type" />
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  </div>
                  <KvTypography color="primary" weight="medium" size="small">{{ t('clients-timeline-email_subject') }} {{ event.content }}</KvTypography>
                </div>
              </template>

              <template #text>

              </template>

              <template #footer>
                <div class="d-flex flex-column gap-md align-items-start">
                  <KvChip v-if="event.automation" rounded="soft" density="compact" class="mr-auto">
                    <KvTypography color="primary">
                      {{ t('clients-detail-automation') }}
                      <KvTypography color="primary" weight="medium">{{ event.automation.title }}</KvTypography>
                    </KvTypography>
                  </KvChip>
                  <KvButton v-if="event.status !== 'sent' && event.status !== 'accepted'" size="small" @click.prevent="sendNotification(event.id)">{{ t('clients-detail-send_now') }}</KvButton>
                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'whatsapp' || event.type === 'whatsapp-cloud'" class="event-content" variant="soft" density="compact" collapsible v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <div class="d-flex align-items-center gap-sm flex-wrap">
                    <NotificationStatusBadge :status="event.status" :fail-reason="event.fail_reason" :delivered-at="event.delivered_at" :read-at="event.read_at" :replied-at="event.replied_at" />
                    <NotificationRecipientChip :address="event.recipient_address" :method="event.type" />
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  </div>
                  <KvTypography color="primary" size="small" weight="medium">{{ getLabel(event) }} {{ t('clients-timeline-whatsapp_message') }}</KvTypography>
                </div>
              </template>

              <template #text>
                <KvMessageRenderer :content="event.content" :options="event.whatsapp_cloud_options" :senders-id="event.places_senders_id" :time="event.time"></KvMessageRenderer>
              </template>

              <template #footer>
                <div class="d-flex flex-column gap-md align-items-start">
                  <KvChip v-if="event.automation" rounded="soft" density="compact" class="mr-auto">
                    <KvTypography color="primary">
                      {{ t('clients-detail-automation') }}
                      <KvTypography color="primary" weight="medium">{{ event.automation.title }}</KvTypography>
                    </KvTypography>
                  </KvChip>
                  <KvButton v-if="event.status !== 'sent' && event.status !== 'accepted'" size="small" @click.prevent="sendNotification(event.id)">{{ t('clients-detail-send_now') }}</KvButton>
                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'sms'" class="event-content" variant="soft" density="compact" collapsible v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <div class="d-flex align-items-center gap-sm flex-wrap">
                    <NotificationStatusBadge :status="event.status" :fail-reason="event.fail_reason" :delivered-at="event.delivered_at" :read-at="event.read_at" :replied-at="event.replied_at" />
                    <NotificationRecipientChip :address="event.recipient_address" :method="event.type" />
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  </div>
                  <KvTypography color="primary" size="small" weight="medium">{{ getLabel(event) }} {{ t('clients-timeline-sms_message') }}</KvTypography>
                </div>
              </template>

              <template #text>
                <KvMessageRenderer :content="event.content" type="sms" :time="event.time"></KvMessageRenderer>
              </template>

              <template #footer>
                <div class="d-flex flex-column gap-md align-items-start">
                  <KvChip v-if="event.automation" rounded="soft" density="compact" class="mr-auto">
                    <KvTypography color="primary">
                      {{ t('clients-detail-automation') }}
                      <KvTypography color="primary" weight="medium">{{ event.automation.title }}</KvTypography>
                    </KvTypography>
                  </KvChip>
                  <KvButton v-if="event.status !== 'sent' && event.status !== 'accepted'" size="small" @click.prevent="sendNotification(event.id)">{{ t('clients-detail-send_now') }}</KvButton>
                </div>
              </template>
            </KvCard>


            <KvCard v-else-if="event.type === 'rcs'" class="event-content" variant="soft" density="compact" collapsible v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <div class="d-flex align-items-center gap-sm flex-wrap">
                    <NotificationStatusBadge :status="event.status" :fail-reason="event.fail_reason" :delivered-at="event.delivered_at" :read-at="event.read_at" :replied-at="event.replied_at" />
                    <NotificationRecipientChip :address="event.recipient_address" :method="event.type" />
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  </div>
                  <KvTypography color="primary" size="small" weight="medium">{{ getLabel(event) }} {{ t('clients-timeline-rcs_message') }}</KvTypography>
                </div>
              </template>
              <template #text>
                <KvMessageRenderer :content="event.content" :options="event.rcs_options" type="rcs" :time="event.time"></KvMessageRenderer>
              </template>
              <template #footer>
                <div class="d-flex flex-column gap-md align-items-start">
                  <KvChip v-if="event.automation" rounded="soft" density="compact" class="mr-auto">
                    <KvTypography color="primary">
                      {{ t('clients-detail-automation') }}
                      <KvTypography color="primary" weight="medium">{{ event.automation.title }}</KvTypography>
                    </KvTypography>
                  </KvChip>
                  <KvButton v-if="event.status !== 'sent' && event.status !== 'accepted'" size="small" @click.prevent="sendNotification(event.id)">{{ t('clients-detail-send_now') }}</KvButton>
                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'task-created'" class="event-content" variant="soft" density="compact" collapsible v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  <KvTypography color="primary" size="small" weight="medium" v-if="event.author"><!-- i18n-validator-ignore-next-line: runtime event author -->{{ event.author }} {{ t('clients-detail-created_task') }} - <!-- i18n-validator-ignore-next-line: runtime task title -->{{ event.title }}</KvTypography>
                  <KvTypography color="primary" size="small" weight="medium" v-else>{{ t('clients-detail-created_task') }} - <!-- i18n-validator-ignore-next-line: runtime task title -->{{ event.title }}</KvTypography>

                </div>
              </template>

              <template #text>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <div v-if="event.suggested_message">
                    <KvTypography truncate color="primary">
                      {{ t('clients-detail-suggested_message') }}
                    </KvTypography>
                    <KvTypography line-break weight="medium" color="primary">
                      <!-- i18n-validator-ignore-next-line: runtime suggested message -->{{ event.suggested_message }}
                    </KvTypography>
                  </div>

                  <KvChip rounded="soft" density="compact" class="mw-100">
                    <KvTypography color="primary">
                      {{ t('clients-detail-due_date') }}
                      <KvTypography color="primary" weight="medium">
                        <HumanDate :date="event.due_date" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf"></HumanDate>
                      </KvTypography>
                    </KvTypography>
                  </KvChip>
                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'task-completed'" class="event-content" variant="soft" density="compact" collapsible v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <div class="d-flex align-items-center gap-sm">
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  </div>
                  <KvTypography color="primary" size="small" weight="medium" v-if="event.author"><!-- i18n-validator-ignore-next-line: runtime event author -->{{ event.author }} {{ t('clients-detail-task_completed') }} - <!-- i18n-validator-ignore-next-line: runtime task title -->{{ event.title }}</KvTypography>
                  <KvTypography color="primary" size="small" weight="medium" v-else>{{ t('clients-detail-task_completed') }} - <!-- i18n-validator-ignore-next-line: runtime task title -->{{ event.title }}</KvTypography>

                </div>
              </template>

              <template #text>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <div v-if="event.suggested_message">
                    <KvTypography truncate color="primary">
                      {{ t('clients-detail-suggested_message') }}
                    </KvTypography>
                    <KvTypography line-break weight="medium" color="primary">
                      <!-- i18n-validator-ignore-next-line: runtime suggested message -->{{ event.suggested_message }}
                    </KvTypography>
                  </div>

                  <KvChip rounded="soft" density="compact" class="mw-100">
                    <KvTypography color="primary">
                      {{ t('clients-detail-due_date') }}
                      <KvTypography color="primary" weight="medium">
                        <HumanDate :date="event.due_date" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf"></HumanDate>

                      </KvTypography>
                    </KvTypography>
                  </KvChip>
                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'campaign-interaction' && event.channel === 'phonetrack'" class="event-content" variant="soft" :id="event.id" density="compact" collapsible v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                <KvTypography size="small" weight="medium">{{ t('clients-detail-phonetrack_call') }}</KvTypography>
              </template>
              <template #text>
                <KvAudioPlayer :source="event.interaction_data.audio_record"></KvAudioPlayer>
              </template>
            </KvCard>
            <KvCard v-else-if="event.type === 'campaign-interaction'" class="event-content" variant="soft" :id="event.id" density="compact" collapsible v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex align-items-center gap-sm">
                  <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  <Icon v-if="event.branch" icon-class="ui-marker-pin-02" size="2" color="#209941" />
                  <!-- i18n-validator-ignore-next-line: runtime branch name -->
                  {{ event.branch }}
                </div>
                <KvTypography size="small" weight="medium">{{ t('clients-timeline-visited_site_from') }} {{ event.title ? event.title : t('clients-timeline-ad') }}</KvTypography>
              </template>
              <template #text>
                <div class="d-flex flex-column gap-md">
                  <div class="d-flex gap-sm align-items-center" v-if="event.utm_source">
                    <Icon icon-class="ui-tag-01" color="#2AAA4C"></Icon>
                    <KvTypography color="primary" weight="medium">{{ t('clients-detail-utm_source') }} {{ event.utm_source }}</KvTypography>
                  </div>

                  <div class="d-flex gap-sm align-items-center" v-if="event.utm_medium">
                    <Icon icon-class="ui-tag-01" color="#2AAA4C"></Icon>
                    <KvTypography color="primary" weight="medium">{{ t('clients-detail-utm_medium') }} {{ event.utm_medium }}</KvTypography>
                  </div>

                  <div class="d-flex gap-sm align-items-center" v-if="event.utm_campaign">
                    <Icon icon-class="ui-tag-01" color="#2AAA4C"></Icon>
                    <KvTypography color="primary" weight="medium">{{ t('clients-detail-utm_campaign') }} {{ event.utm_campaign }}</KvTypography>
                  </div>

                  <div class="d-flex gap-sm align-items-center" v-if="event.gclid">
                    <div>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.0007 8.15546C15.0007 7.57991 14.953 7.15991 14.8499 6.72437H8.14355V9.32211H12.08C12.0007 9.96769 11.5721 10.9399 10.6197 11.5932L10.6064 11.6802L12.7268 13.29L12.8737 13.3044C14.2229 12.0832 15.0007 10.2866 15.0007 8.15546" fill="#4285F4" />
                        <path d="M8.14315 15C10.0717 15 11.6907 14.3778 12.8733 13.3044L10.6193 11.5933C10.0162 12.0055 9.20662 12.2933 8.14315 12.2933C6.25428 12.2933 4.65113 11.0722 4.07965 9.3844L3.99588 9.39137L1.79104 11.0636L1.76221 11.1422C2.93679 13.4288 5.34949 15 8.14315 15Z" fill="#34A853" />
                        <path d="M4.07982 9.38442C3.92903 8.94887 3.84176 8.48217 3.84176 7.99997C3.84176 7.51772 3.92903 7.05107 4.07189 6.61552L4.06789 6.52276L1.83542 4.82367L1.76237 4.85772C1.27827 5.80662 1.00049 6.87219 1.00049 7.99997C1.00049 9.12774 1.27827 10.1933 1.76237 11.1422L4.07982 9.38442" fill="#FBBC05" />
                        <path d="M8.14319 3.70665C9.48444 3.70665 10.3892 4.27442 10.9051 4.7489L12.9209 2.82C11.6829 1.69222 10.0717 1 8.14319 1C5.34951 1 2.9368 2.5711 1.76221 4.85775L4.07173 6.61555C4.65114 4.92777 6.25431 3.70665 8.14319 3.70665" fill="#EB4335" />
                      </svg>
                    </div>

                    <KvTypography color="primary" weight="medium" truncate>
                      {{ t('clients-detail-gclid') }}
                      <KvTypography color="primary" weight="medium" line-break>{{ event.gclid }}</KvTypography>
                    </KvTypography>
                  </div>

                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'coupon_generated'" class="event-content" variant="soft" density="compact">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  <KvTypography color="primary" size="small" weight="medium">{{ t('clients-timeline-generated_coupon') }} {{ event.text }} {{ t('clients-timeline-with_code') }} {{ event.code }}</KvTypography>
                </div>
              </template>

              <template #text>
                <KvChip v-if="event.automation" rounded="soft" density="compact" class="mr-auto">
                  <KvTypography color="primary">
                    {{ t('clients-detail-automation') }}
                    <KvTypography color="primary" weight="medium">{{ event.automation.title }}</KvTypography>
                  </KvTypography>
                </KvChip>

              </template>
            </KvCard>
            <KvCard v-else-if="event.type === 'coupon_used'" class="event-content" variant="soft" density="compact">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  <KvTypography color="primary" size="small" weight="medium">{{ t('clients-detail-redeemed_bonus_of') }} {{ event.text }}</KvTypography>
                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'earned_points'" class="event-content" variant="soft" density="compact" collapsible v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  <KvTypography v-if="event.points >= 0" color="primary" size="small" weight="medium">
                    {{ t('clients-timeline-earned_points') }} {{ printPoints(event.points) }}!
                  </KvTypography>
                  <KvTypography v-else color="primary" size="small" weight="medium">
                    {{ t('clients-timeline-lost_points') }} {{ printPoints(event.points * -1) }}!
                  </KvTypography>
                </div>
              </template>

              <template #text>
                <KvChip v-if="event.automation" rounded="soft" density="compact" class="mr-auto">
                  <KvTypography color="primary">
                    {{ t('clients-detail-automation') }}
                    <KvTypography color="primary" weight="medium">{{ event.automation.title }}</KvTypography>
                  </KvTypography>
                </KvChip>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'rescued_reward'" class="event-content" variant="soft" density="compact">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <div class="d-flex align-items-center gap-sm">
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  </div>
                  <KvTypography color="primary" size="small" weight="medium">
                    {{ t('clients-detail-exchanged') }}
                    {{ printPoints(event.usedPoints) }}
                    {{ t('clients-detail-for') }}
                    {{ event.text }}
                  </KvTypography>
                </div>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'ai-action'" collapsible class="event-content" variant="soft" :id="`${event.type}-${event.id}`" density="compact" v-model="openCards[`${event.type}-${event.id}`]">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm">
                  <div class="d-flex align-items-center gap-sm flex-wrap">
                    <KvChip
                      v-if="event.model_tier || event.model"
                      :color="event.result_success ? 'success' : 'error'"
                      density="extra-compact"
                      :prepend-icon="event.result_success ? 'ui-check' : 'ui-x-square'"
                    >
                      {{ event.model_tier === 'basic' ? t('clients-timeline-ai_basic') : event.model_tier === 'advanced' ? t('clients-timeline-ai_advanced') : event.model }}
                    </KvChip>
                    <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  </div>
                  <KvTypography size="small" weight="semibold" color="primary">{{ t('clients-timeline-ai_generated_content') }}</KvTypography>
                </div>
              </template>
              <template #text>
                <div class="d-flex flex-column gap-sm ai-action-detail">
                  <div v-if="aiActionMessage(event)" class="ai-message-bubble-wrap">
                    <KvMessageRenderer :content="aiActionMessage(event)" type="whatsapp" :time="event.time" max-height="200" />
                  </div>

                  <div v-if="aiActionExtraFields(event).length > 0" class="ai-extra-fields">
                    <div v-for="field in aiActionExtraFields(event)" :key="field.key">
                      <KvTypography size="small" color="muted" weight="medium">{{ field.key }}:</KvTypography>
                      <pre class="tool-call-json">{{ field.value }}</pre>
                    </div>
                  </div>

                  <div class="d-flex gap-sm flex-wrap mt-1">
                    <KvButton variant="outlined" size="small" @click.stop="toggleToolLog(event.id)">
                      {{ t('clients-timeline-view_execution_details') }}
                    </KvButton>
                  </div>

                  <div v-if="showToolLog[event.id]" class="ai-execution-details mt-2">
                    <div v-if="event.instruction" class="mb-2">
                      <KvTypography size="small" color="muted" weight="semibold">{{ t('clients-timeline-instruction') }}</KvTypography>
                      <KvTypography size="small" style="white-space: pre-wrap;">{{ event.instruction }}</KvTypography>
                    </div>

                    <div v-if="event.tool_calls && event.tool_calls.length > 0">
                      <KvButton variant="outlined" size="small" class="mb-2" @click.stop="toggleFullPrompt(event.id)">
                        {{ t('clients-timeline-tool_calls', [event.tool_calls.length]) }}
                      </KvButton>

                      <div v-if="showFullPrompt[event.id]" class="tool-calls-log">
                        <div v-for="(call, idx) in event.tool_calls" :key="idx" class="tool-call-item">
                          <div class="tool-call-header">
                            <KvChip density="extra-compact" :color="call.result && call.result.error ? 'error' : 'default'">
                              {{ call.tool }}
                            </KvChip>
                            <KvTypography size="small" color="muted">{{ call.duration_ms }}ms</KvTypography>
                          </div>
                          <div class="tool-call-body">
                            <div v-if="call.arguments && Object.keys(call.arguments).length > 0">
                              <KvTypography size="small" color="muted" weight="medium">{{ t('clients-timeline-arguments') }}</KvTypography>
                              <pre class="tool-call-json">{{ JSON.stringify(call.arguments, null, 2) }}</pre>
                            </div>
                            <div v-if="call.result">
                              <KvTypography size="small" color="muted" weight="medium">{{ t('clients-timeline-result') }}</KvTypography>
                              <pre class="tool-call-json">{{ JSON.stringify(call.result, null, 2) }}</pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template #footer>
                <KvChip v-if="event.automation" rounded="soft" density="compact" class="mr-auto">
                  <KvTypography color="primary">
                    {{ t('clients-detail-automation') }}
                    <KvTypography color="primary" weight="medium">{{ event.automation.title }}</KvTypography>
                  </KvTypography>
                </KvChip>
              </template>
            </KvCard>

            <KvCard v-else-if="event.type === 'field-update' && event.status === 'failed'" class="event-content" variant="soft" density="compact">
              <template #subtitle>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <HumanDate :date="event.time" format="[d]/[M]/[Y] às [h]:[m] - [D]:ucf" />
                  <KvTypography color="primary" size="small" weight="medium">{{ t('clients-timeline-field_update_failed') }}</KvTypography>
                </div>
              </template>

              <template #text>
                <div class="d-flex flex-column gap-sm align-items-start">
                  <KvTypography v-if="event.fail_reason" color="primary" size="small">
                    {{ t('clients-timeline-field_update_failed_reason') }} {{ event.fail_reason }}
                  </KvTypography>
                  <KvChip v-if="event.automation" rounded="soft" density="compact" class="mr-auto">
                    <KvTypography color="primary">
                      {{ t('clients-detail-automation') }}
                      <KvTypography color="primary" weight="medium">{{ event.automation.title }}</KvTypography>
                    </KvTypography>
                  </KvChip>
                </div>
              </template>
            </KvCard>

            <KvCard v-else class="event-content" variant="soft" density="compact">
              <template #text>{{ t('clients-timeline-unknown_event') }} {{ event.type }}</template>
            </KvCard>

          </div>
        </template>

        <div v-else-if="timeline && eventsWithMeta.length === 0" class="d-flex flex-column align-items-center justify-content-center gap-md text-center py-4">
          <div class="empty-state-icon d-flex align-items-center justify-content-center">
            <Icon icon-class="ui-clock" size="15" color="#c4c4c8" />
          </div>

          <div class="d-flex flex-column gap-sm">
            <KvTypography size="extra-large">
              {{ t('clients-timeline-empty_title') }}
            </KvTypography>

            <KvTypography size="small" color="muted">
              {{ t('clients-timeline-empty_subtitle') }}
            </KvTypography>
          </div>

        </div>

        <KvPagination v-if="timeline" @page-change="onPageChange" @items-per-page-change="onItemsPerPageChange" :total="total" :currentPage="currentPage" :itemsPerPage="limit" show-items-per-page :showInfo="false"></KvPagination>
      </KvScrollbar>
    </div>

    <drawer
      ref="answerDrawer"
      id="client-timeline-answer-drawer"
      size="xl"
      :has-header="false"
      :backdrop="false"
      @hide="activeAnswer = null"
    >
      <template #body="{ close }">
        <message
          v-if="activeAnswer"
          :id="activeAnswer"
          :hide-client-section="true"
          @close="close"
          @duplicated="onAnswerDuplicated"
        />
      </template>
    </drawer>
  </div>
</template>

<script>
import KvButton from '@/components/form/kv-button.vue';
import Icon from '@/components/icon/icon.vue';
import KvInput from '@/components/inputs/kv-input.vue';
import KvCard from '@/components/ui/kv-card.vue';
import KvChip from '@/components/ui/kv-chip.vue';
import KvAudioPlayer from '@/components/ui/kv-audio-player.vue';
import KvTypography from '@/components/ui/kv-typography.vue';
import KvHtmlRenderer from '@/components/ui/kv-html-renderer.vue';
import KvMessageRenderer from '@/components/ui/kv-message-renderer.vue';
import KvScrollbar from '@/components/ui/kv-scrollbar.vue';
import { mapEvent } from '@/helpers/mapper';
import HumanDate from '@/components/human-date/human-date.vue';
import keviApi from '@/keviApi';
import KvFilter from "@/components/reports/kv-filter-bar.vue";
import KvPagination from '@/components/ui/kv-pagination.vue';
import debounce from '@/helpers/debounce';
import AddOrder from "@/dashboard/add-order/add-order.vue";
import KvSkeleton from "@/components/ui/kv-skeleton.vue";
import KvTooltip from "@/components/ui/kv-tooltip.vue";
import NotificationStatusBadge from "@/components/ui/notification-status-badge.vue";
import NotificationRecipientChip from "@/components/ui/notification-recipient-chip.vue";
import currencyMixin from "@/mixins/currency-mixin";
import Drawer from "@/components/drawer/drawer.vue";
import Message from "@/inbox/_message.vue";

export default {
  mixins: [currencyMixin],
  props: {
    client: Object,
    timeline: Object,
    loadTimeline: Function,
    orderFields: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  components: {
    KvInput,
    Icon,
    KvCard,
    KvChip,
    KvTypography,
    KvButton,
    KvHtmlRenderer,
    KvMessageRenderer,
    KvAudioPlayer,
    HumanDate,
    KvScrollbar,
    KvFilter,
    KvPagination,
    AddOrder,
    KvSkeleton,
    KvTooltip,
    NotificationStatusBadge,
    NotificationRecipientChip,
    Drawer,
    Message,
  },
  data() {
    return {
      search: this.$route.query.search || "",
      quotationStatusLabel: {
        "expired": this.$root.t("clients-detail-quotation_status_expired"),
        "rejected": this.$root.t("clients-detail-quotation_status_rejected")
      },
      toDeleteOrder: null,
      toDeleteCustomEvent: null,
      toUpdateOrder: null,

      openCards: {},
      showToolLog: {},
      showFullPrompt: {},
      openCard: false,
      extraFilters: [],
      currentPage: parseInt(this.$route.query.page) || 1,
      limit: parseInt(this.$route.query.limit) || 50,
      filters: {},
      activeAnswer: null,
    }
  },
  mounted() {
    this.extraFilters.push({
      component: "period",
      label: this.$root.t('filter_bar-period'),
      name: "period",
      removable: true,
    });
  },
  computed: {
    isInitialLoading() {
      // Se está carregando E não temos timeline (primeiro load), retorna true.
      // Se está carregando mas já temos timeline (paginação), retorna false.
      return this.loading && !this.timeline;
    },
    allCollapsed() {
      const values = Object.values(this.openCards);
      if (!values.length) return false;
      return values.every(v => v === false);
    },
    dashboardLevel() {
      return this.$store.state.user.permissions.dashboard.options.level;
    },
    eventsWithMeta() {
      if (!this.timeline || !Array.isArray(this.timeline.events)) {
        return [];
      }
      return this.timeline.events.map(event => ({
        ...event,
        meta: mapEvent(event.type, { status: event.status, action: event.action, channel: event.channel, points: event.points, result_success: event.result_success }),
      }))
    },
    user() {
      return this.$store.state.user;
    },
    users() {
      return this.$store.state.users;
    },
    total() {
      return this.timeline?.eventsCount?.total ?? 50;
    },
  },
  methods: {
    openAnswer(id, e) {
      // Let the browser handle new-tab clicks through the inbox href.
      if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) {
        return;
      }
      if (e) e.preventDefault();
      if (id == null || id === "") return;
      // Keep as string — snowflake IDs exceed JS safe integer range.
      this.activeAnswer = String(id);
      this.$nextTick(() => {
        if (this.$refs.answerDrawer) {
          this.$refs.answerDrawer.open();
        }
      });
    },
    answerInboxHref(id) {
      return this.$router.resolve({
        path: '/inbox',
        query: {
          filter: JSON.stringify({ client: this.client.id }),
          message_id: id,
        },
      }).href;
    },
    onAnswerDuplicated() {
      this.updateTimeline(false);
    },
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    async updateFilters(filters) {
      this.filters = filters;
      this.updateTimeline();
    },
    async updateTimeline(scrollToTop = true) {
      await this.loadTimeline({ page: this.currentPage, limit: this.limit, search: this.search, filters: this.filters });
      if (scrollToTop) this.$refs.scrollbar?.scrollToTop();
    },
    updateUrlParams() {
      const query = {
        ...this.$route.query,
        page: this.currentPage,
        limit: this.limit,
      };

      if (this.search) {
        query.search = this.search;
      } else {
        delete query.search;
      }

      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => { });
      }
    },
    async onPageChange(currentPage) {
      this.currentPage = currentPage;
      this.updateUrlParams();
      await this.updateTimeline();
    },
    async onItemsPerPageChange(limit) {
      this.limit = limit;
      this.currentPage = 1;
      this.updateUrlParams();
      await this.updateTimeline();
      this.$refs.scrollbar?.scrollToTop();
    },
    toggleAll() {
      const shouldOpen = this.allCollapsed;
      Object.keys(this.openCards).forEach(key => {
        this.openCards[key] = shouldOpen;
      });
    },
    toggleToolLog(eventId) {
      this.$set(this.showToolLog, eventId, !this.showToolLog[eventId]);
    },
    toggleFullPrompt(eventId) {
      this.$set(this.showFullPrompt, eventId, !this.showFullPrompt[eventId]);
    },
    aiActionMessage(event) {
      if (event.result_payload) {
        return event.result_payload.message || event.result_payload.conteudo || '';
      }
      return '';
    },
    aiActionExtraFields(event) {
      if (!event.result_payload) return [];
      return Object.entries(event.result_payload)
        .filter(([key]) => key !== 'message' && key !== 'conteudo')
        .map(([key, value]) => ({ key, value }));
    },
    getLabel(event) {
      const isPending =
        this.isFuture(event.time) ||
        ['scheduled', 'queued'].includes(event.status)
      if (isPending) {
        let label = this.t("clients-timeline-waiting")
        if (event.status === 'scheduled') {
          label = this.t("clients-timeline-scheduled_feminine");
        } else if (event.status === 'queued') {
          label = this.t("clients-detail-status_processing");
        }
        return label;
      }
      if (event.status === 'accepted') {
        return this.t("clients-timeline-waiting");
      }
      if (event.status === 'failed') {
        return this.t("clients-timeline-failed_of");
      }
      return this.t("clients-timeline-received")
    },
    getChipData(event) {
      const isPending =
        this.isFuture(event.time) ||
        ['scheduled', 'queued'].includes(event.status)
      if (isPending) {
        let label = this.t("clients-timeline-pending")
        if (event.status === 'scheduled') {
          label = this.t("clients-detail-status_scheduled");
        } else if (event.status === 'queued') {
          label = this.t("clients-detail-status_processing");
        }
        return { label: label, color: 'default' }
      }
      if (event.status === 'accepted') {
        return { label: this.t('clients-detail-status_accepted'), color: 'default' }
      }
      if (event.status === 'failed') {
        return { label: this.t('clients-detail-status_failed'), color: 'error' }
      }
      return { label: this.t('clients-detail-status_sent'), color: 'success' }
    },
    mapEvent,
    scrollToElement(id) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },
    formatQuantity(quantity) {
      return Math.round(quantity * 100) / 100;
    },
    getOrderFieldEntries(event) {
      if (!event?.fields) return [];
      return Object.values(event.fields).filter(
        f => f.value !== null && f.value !== undefined && f.value !== ''
      );
    },
    formatOrderFieldDisplay(entry) {
      if (!entry || entry.value === null || entry.value === undefined || entry.value === '') return '';

      const orderFields = Array.isArray(this.orderFields) ? this.orderFields : [];
      const field = orderFields.find((item) =>
        String(item.id) === String(entry.orders_fields_id) ||
        String(item.field_identifier) === String(entry.field_identifier)
      );
      const fieldType = entry.field_type || field?.type;
      let parsedValue = entry.value;

      if (fieldType === 'checkbox') {
        if (typeof parsedValue === 'string') {
          try {
            parsedValue = JSON.parse(parsedValue);
          } catch (e) {
            parsedValue = [parsedValue];
          }
        }
        if (Array.isArray(parsedValue)) {
          return parsedValue
            .map((item) => item?.text || item?.label || item?.value || item)
            .filter(Boolean)
            .join(', ');
        }
      }

      return this.formatOrderFieldValue({
        ...(field || {}),
        type: fieldType,
      }, fieldType === 'checkbox' ? parsedValue : entry.value, entry.user);
    },
    formatOrderFieldValue(field, value, user = null) {
      if (value === null || value === undefined || value === '') return '';
      if (field.type === 'checkbox' && Array.isArray(value)) {
        return value
          .map(v => v?.text || v?.label || v?.value || v)
          .filter(Boolean)
          .join(', ');
      }
      if (field.type === 'date') {
        try {
          const d = new Date(value);
          if (!isNaN(d.getTime())) {
            return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
          }
        } catch (e) {}
      }
      if (field.type === 'user') {
        if (user?.name) {
          return user.name;
        }
        const option = Array.isArray(field.options)
          ? field.options.find(o => String(o.value) === String(value))
          : null;
        return option ? option.label : value;
      }
      return value;
    },
    printPoints: function (points) {
      return parseInt(points) === 1
        ? points + this.t("clients-detail-point_singular")
        : points + this.t("clients-detail-point_plural");
    },
    modalUpdateOrder(order) {
      this.toUpdateOrder = null;
      this.toUpdateOrder = order;
      this.$refs.updateOrder.open();
    },
    confirmDeleteOrder: function (order) {
      this.toDeleteOrder = order;
      this.$refs.confirmDeleteOrder.open();
    },
    deleteOrder: function () {
      this.$http(
        keviApi.getOptions(`orders/${this.toDeleteOrder.id}`, "DELETE"),
      ).then(() => {
        this.updateTimeline(false);
        this.toDeleteOrder = null;
      });
    },
    confirmDeleteCustomEvent: function (event) {
      this.toDeleteCustomEvent = event;
      this.$refs.confirmDeleteCustomEvent.open();
    },
    deleteCustomEvent: function () {
      if (this.user.global_admin) {
        let options = keviApi.getOptions(
          "custom-event/unregister-event",
          "POST",
          {
            custom_event_clients_id: this.toDeleteCustomEvent.id,
          },
        );
        this.$http(options).then(function () {
          this.updateTimeline(false);
          this.toDeleteCustomEvent = null;
        });
      }
    },
    afterUpdateOrder() {
      this.updateTimeline(false);
      this.toUpdateOrder = null;
      this.$refs.updateOrder.close();
    },
    sendNotification(id) {
      var options = keviApi.getOptions("notifications/send", "POST", {
        id: id,
      });
      try {
        this.$http(options).then(
          (response) => {
            if (response.status === 200) {
              this.$store.dispatch(
                "notification/success",
                this.t("clients-detail-notification_sent_success"),
              );
              this.updateTimeline(false);
            } else {
              this.$store.dispatch(
                "notification/warning",
                this.t("clients-detail-notification_send_error_prefix") +
                response.body.error,
              );
            }
          },
          (error) => {
            this.$store.dispatch(
              "notification/warning",
              this.t("clients-detail-notification_send_error_prefix") + error.body.error,
            );
          },
        );
      } catch (error) {
        this.$store.dispatch(
          "notification/warning",
          this.t("clients-detail-notification_send_error_prefix") + (error?.body?.error || error?.message || this.t("clients-detail-unknown_error")),
        );
      }
    },
    isFuture: function (date) {
      date = new Date(date);
      let now = new Date();
      return date > now;
    },
    parseFailReason(reason) {
      if (reason === "not-subscribed") {
        return this.t("clients-detail-fail_reason_not_subscribed");
      }
      if (reason === "not-enough-credits") {
        return this.t("clients-detail-fail_reason_not_enough_credits");
      }
      if (!reason) {
        return this.t("clients-detail-unknown_error");
      }
      return reason;
    },
  },
  watch: {
    search: {
      handler: debounce(function () {
        this.currentPage = 1;
        this.updateUrlParams();
        this.updateTimeline();
      }, 1000),
    },
    eventsWithMeta: {
      immediate: true,
      handler(events) {
        const state = {};

        events.forEach(event => {
          const key = `${event.type}-${event.id}`;
          state[key] = false;
        });

        this.openCards = state;
      }
    }
  }
};
</script>

<style lang="less">
.timeline-wrapper {
  display: grid;
}

.product-category {
  opacity: 0.7;
}

.events-timeline {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: 100%;
  height: 100dvh;

  @media (max-width: 640px) {
    max-height: 60dvh;
  }

  .list {
    margin: 0;
    padding-left: 20px;
  }

  .event-icon-wrapper {
    position: relative;

    .event-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 33px;
      height: 33px;
      border-radius: 50%;
      z-index: 1;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: -48px;
      left: 50%;
      transform: translateX(-50%);
      width: 2px;
      background-color: #E5E7EB;
      z-index: 0;
    }
  }

  .event-item {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .event-item:last-child {
    .event-icon-wrapper::before {
      bottom: 0;
    }
  }

  .event-content {
    flex: 1;
    min-width: 0;
    position: relative;

    >.kv-card-header>.kv-card-header-inner {
      min-width: 0;
    }
  }

  .ai-action-detail {
    .ai-message-bubble-wrap {
      border-radius: 16px;
      background: #f3f4f6;
      overflow: hidden;
      padding: 16px;

      .kv-message-wrapper.kv-message-whatsapp {
        background-image: none !important;
        background-color: transparent !important;
        background: transparent !important;
      }

      .wpp-content-wrapper {
        padding: 12px 14px 14px 16px;
      }

      .kv-time,
      .wpp-embedded-time {
        color: #6C737F;
      }
    }

    .ai-extra-fields {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .ai-execution-details {
      border-top: 1px solid #e5e7eb;
      padding-top: 8px;
    }

    .tool-calls-log {
      border-left: 3px solid #e5e7eb;
      padding-left: 12px;
    }

    .tool-call-item {
      padding: 6px 0;

      &:not(:last-child) {
        border-bottom: 1px dashed #e5e7eb;
      }
    }

    .tool-call-header {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .tool-call-body {
      margin-top: 4px;
      padding-left: 4px;
    }

    .tool-call-json {
      font-size: 11px;
      background: #f3f4f6;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      padding: 6px 8px;
      margin: 4px 0;
      overflow-x: auto;
      max-height: 200px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

}
</style>
